import { AuthService } from './auth'

export class PaymentsService {
  // Helper method to parse JSON response regardless of content-type
  static async parseJsonResponse (response) {
    let text
    try {
      text = await response.text()

      // Check if response is HTML (likely a redirect)
      if (text.includes('<html>') || text.includes('<!DOCTYPE html>')) {
        console.warn('⚠️ API returned HTML instead of JSON. Possible redirect:')
        console.log('HTML Response:', text.substring(0, 500) + '...')

        // Try to extract redirect URL from HTML
        const redirectMatch = text.match(/url='([^']+)'/)
        if (redirectMatch) {
          const redirectUrl = redirectMatch[1]
          console.log('🔄 Extracted redirect URL:', redirectUrl)

          // Check if it's a failure redirect
          if (redirectUrl.includes('payment-failed')) {
            throw new Error(`API redirected to failure page: ${redirectUrl}`)
          }
        }

        throw new Error('API returned HTML instead of JSON - possible redirect or error page')
      }

      return JSON.parse(text)
    } catch (parseError) {
      const contentType = response.headers.get('content-type')
      console.error('Failed to parse JSON response:', parseError)
      console.error('Content-Type:', contentType)
      console.error('Response text:', text)
      throw new Error('Invalid JSON response')
    }
  }

  /**
   * Get payment history
   * GET https://api.quali-d.com/payments/history
   * Authorization: Bearer token header
   */
  static async getPaymentHistory () {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/payments/history', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        totalPayments: data.total_payments || 0,
        payments: data.payments || [],
        message: data.message || 'Payment history retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching payment history:', error)
      return {
        success: false,
        totalPayments: 0,
        payments: [],
        message: 'Failed to fetch payment history',
        error: error.message
      }
    }
  }

  /**
   * Get subscription status
   * GET https://api.quali-d.com/payments/subscription-status
   * Authorization: Bearer token header
   */
  static async getSubscriptionStatus () {
    try {
      const token = AuthService.getAuthToken() || localStorage.getItem('agent_token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/payments/subscription-status', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success === true,
        subscription: data.subscription || {},
        message: data.message || 'Subscription status retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error)
      return {
        success: false,
        subscription: {},
        message: 'Failed to fetch subscription status',
        error: error.message
      }
    }
  }

  /**
   * Get available plans
   * GET https://dashboard.quali-d.com/api/list_plans.php
   */
  static async getAvailablePlans () {
    try {
      const response = await fetch('https://dashboard.quali-d.com/api/list_plans.php', {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: true,
        plans: Array.isArray(data) ? data : [],
        message: 'Plans retrieved successfully'
      }
    } catch (error) {
      console.error('Error fetching available plans:', error)
      return {
        success: false,
        plans: [],
        message: 'Failed to fetch available plans',
        error: error.message
      }
    }
  }

  /**
   * Initiate renewal subscription
   * POST https://api.quali-d.com/payments/initiate-renewal
   * Authorization: Bearer token header
   * Body: { cardNumber, expiryYear, expiryMonth, cvv, plan_id }
   */
  static async initiateRenewal (renewalData) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('https://api.quali-d.com/payments/initiate-renewal', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(renewalData)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        merchantRefNumber: data.merchant_ref_number || '',
        redirectUrl: data.redirect_url || '',
        message: data.message || 'Renewal initiated successfully'
      }
    } catch (error) {
      console.error('Error initiating renewal:', error)
      return {
        success: false,
        merchantRefNumber: '',
        redirectUrl: '',
        message: 'Failed to initiate renewal',
        error: error.message
      }
    }
  }

  /**
   * Confirm payment (registration)
   * GET https://api.quali-d.com/payments/confirm-payment?ref={merchantRefNumber}
   * Authorization: Bearer token header (if available)
   */
  static async confirmPayment (merchantRefNumber) {
    try {
      const token = AuthService.getAuthToken()
      const headers = {
        Accept: 'application/json'
      }

      // Add auth header if token exists (for renewals)
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      const response = await fetch(`https://api.quali-d.com/payments/confirm-payment?ref=${merchantRefNumber}`, {
        method: 'GET',
        headers,
        mode: 'cors'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await this.parseJsonResponse(response)
      return {
        success: data.success || true,
        user: data.user || null,
        message: data.message || 'Payment confirmed successfully'
      }
    } catch (error) {
      console.error('Error confirming payment:', error)
      return {
        success: false,
        user: null,
        message: 'Failed to confirm payment',
        error: error.message
      }
    }
  }

  /**
   * Confirm renewal payment
   * GET https://api.quali-d.com/payments/confirm-renewal?ref={merchantRefNumber}
   * Authorization: Bearer token header
   */
  static async confirmRenewal (merchantRefNumber) {
    try {
      const token = AuthService.getAuthToken()
      if (!token) {
        throw new Error('No authentication token found')
      }

      console.log('🔄 Calling confirm renewal API with ref:', merchantRefNumber)
      console.log('🔑 Token present:', token ? 'Yes' : 'No')

      const response = await fetch(`https://api.quali-d.com/payments/confirm-renewal?ref=${merchantRefNumber}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json'
        },
        mode: 'cors'
      })

      console.log('📊 Response status:', response.status)
      console.log('📋 Response headers:', Object.fromEntries(response.headers.entries()))

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ Renewal confirmation failed:', {
          status: response.status,
          response: errorText
        })
        throw new Error(`HTTP error! status: ${response.status}, response: ${errorText.substring(0, 200)}...`)
      }

      const data = await this.parseJsonResponse(response)
      console.log('✅ Renewal confirmation success:', data)
      return {
        success: data.success || true,
        subscription: data.subscription || null,
        message: data.message || 'Renewal confirmed successfully'
      }
    } catch (error) {
      console.error('Error confirming renewal:', error)
      return {
        success: false,
        subscription: null,
        message: 'Failed to confirm renewal',
        error: error.message
      }
    }
  }
}
